import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import ButtonCell from '@app/src/components/Table/Cells/ButtonCell'
import ValueCell from '@app/src/components/Table/Cells/ValueCell'
import ValueCellWithButton from '@app/src/components/Table/Cells/ValueCellWithButton'
import { CONFIGURATION_PAGE_IDS } from '@app/src/pages/Configurations/ConfigurationsScene'
import type { SimpleOrganization } from '@app/src/types/organizations'
import { SolutionType } from '@app/src/types/resourceExplorer'
import paths from '@app/src/wf-constants/paths'
import AddIcon from '@mui/icons-material/Add'
import { Button, TableRow } from '@mui/material'
import React from 'react'
import { generatePath } from 'react-router'
import EditOrganizationContractedValueDrawer from './EditOrganizationContractedValueDrawer'
import EditOrganizationCsmSettingsDrawer from './EditOrganizationCsmSettingsDrawer'
interface UserRowProps {
  row: SimpleOrganization
}

export const OrganizationRow = ({ row: organization }: UserRowProps): JSX.Element => {
  const { openDrawer, closeDrawer } = useDrawer()
  const allowedProviderScreenings = organization.setting?.allowedProviderScreenings

  const isTransparency = organization.setting?.solutionType === SolutionType.Transparency
  const contractedUsers = organization.setting?.contractedUsers
  const actualUsers = organization.actualUsers
  const exceedsContract =
    !isTransparency &&
    contractedUsers !== undefined &&
    contractedUsers !== null &&
    actualUsers !== undefined &&
    actualUsers > contractedUsers

  const contractedSuppliers = organization.setting?.contractedSuppliers
  const actualSuppliers = organization.actualSuppliers
  const exceedsSupplierContract =
    !isTransparency &&
    contractedSuppliers !== undefined &&
    contractedSuppliers !== null &&
    actualSuppliers !== undefined &&
    actualSuppliers > contractedSuppliers

  return (
    <TableRow>
      <ValueCell value={organization.id} />
      <ButtonCell
        to={generatePath(paths.configurations, {
          configurationsPage: CONFIGURATION_PAGE_IDS.Organizations,
          configurationsSubPage: organization.id,
        })}
        label={`${organization.name}`}
      />
      <ValueCell value={organization.vatNumber} />
      <ValueCell value={organization.websiteAddress} />
      <ValueCell value={organization.setting?.solutionType} />
      <ValueCellWithButton
        key={`screenings-${allowedProviderScreenings}`}
        value={allowedProviderScreenings}
        actionButton={
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              openDrawer(<EditOrganizationCsmSettingsDrawer closeDrawer={closeDrawer} organization={organization} />, {
                showAboveDialogs: true,
              })
            }}
            variant="outlined"
            disabled={organization.setting?.solutionType === SolutionType.Transparency}
          >
            Add
          </Button>
        }
      />
      <ValueCellWithButton
        key={`contracted-${contractedUsers}`}
        value={contractedUsers ?? undefined}
        actionButton={
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              openDrawer(
                <EditOrganizationContractedValueDrawer
                  closeDrawer={closeDrawer}
                  organization={organization}
                  field="contractedUsers"
                  title="Set contracted users"
                  currentValueLabel="Current contracted users"
                  setValueLabel="Set the contracted number of users:"
                  saveLabel="Save contracted users"
                />,
                {
                  showAboveDialogs: true,
                },
              )
            }}
            variant="outlined"
            disabled={isTransparency}
          >
            Edit
          </Button>
        }
      />
      <ValueCell
        value={actualUsers}
        color={exceedsContract ? 'error' : undefined}
        fontWeight={exceedsContract ? 'bold' : undefined}
      />
      <ValueCellWithButton
        key={`contracted-suppliers-${contractedSuppliers}`}
        value={contractedSuppliers ?? undefined}
        actionButton={
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              openDrawer(
                <EditOrganizationContractedValueDrawer
                  closeDrawer={closeDrawer}
                  organization={organization}
                  field="contractedSuppliers"
                  title="Set contracted suppliers"
                  currentValueLabel="Current contracted suppliers"
                  setValueLabel="Set the contracted number of suppliers:"
                  saveLabel="Save contracted suppliers"
                />,
                {
                  showAboveDialogs: true,
                },
              )
            }}
            variant="outlined"
            disabled={isTransparency}
          >
            Edit
          </Button>
        }
      />
      <ValueCell
        value={actualSuppliers}
        color={exceedsSupplierContract ? 'error' : undefined}
        fontWeight={exceedsSupplierContract ? 'bold' : undefined}
      />
    </TableRow>
  )
}

export default OrganizationRow
